import { useCallback, useMemo } from "react";
import { useMistralNarrativeOverlay } from "@/hooks/useMistralNarrativeOverlay";
import {
  bulletsToActionRegister,
  bulletsToSignalCards,
  bulletsToEnrolmentRecommendations,
  bulletsToNarrativeItems,
  bulletsToRootCauseCards,
} from "@/utils/mistralInsightMappers";
import {
  Star,
  Users,
  School,
  Award,
  AlertTriangle,
  Monitor,
  Activity,
  TrendingUp,
  Map,
} from "lucide-react";
import {
  buildReadinessGaugeStyle,
  buildTeacherReadinessScore,
  buildReadinessLines,
  buildInsightStatusChips,
  buildInsightKpis,
  buildPrioritySignals,
  buildRootCauseSignals,
  buildAiNarrativeSummary,
  buildRecommendations,
  buildActionQueue,
  buildImpactMatrix,
  buildTeacherInsightsMarkdown,
} from "@/utils/teacherInsightsDerived";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import { cardsToInsightLines, registerToInsightLines } from "@/utils/insightActionPlanDefaults";
import { toTitleCase } from "../utils/toTitleCase";

const KPI_ICONS = {
  "": Users,
  green: School,
  purple: Award,
  orange: AlertTriangle,
};

const SIGNAL_ICONS = {
  purple: Award,
  green: Monitor,
  orange: Activity,
};

const CAUSE_ICONS = {
  purple: Award,
  orange: Users,
  green: TrendingUp,
  "": Map,
};

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const TeacherInsightsTab = ({ overview = {} }) => {
  const buildLocalMarkdown = useCallback(
    () => buildTeacherInsightsMarkdown(overview),
    [overview]
  );
  const scopeKey = useMemo(
    () => JSON.stringify({ t: overview?.teachers_cy, q: overview?.teacher_quality_index }),
    [overview?.teachers_cy, overview?.teacher_quality_index]
  );
  const { loading: aiLoading, isMistral, pickMapped, markdown: aiMarkdown } = useMistralNarrativeOverlay({
    kind: "teacher-insights",
    title: "Teacher Intelligence Briefing",
    buildLocalMarkdown,
    context: { overview },
    enabled: Boolean(overview?.teachers_cy),
    scopeKey,
  });

  const readiness = useMemo(() => buildTeacherReadinessScore(overview), [overview]);
  const gaugeStyle = useMemo(() => buildReadinessGaugeStyle(readiness), [readiness]);
  const readinessLines = useMemo(() => buildReadinessLines(overview), [overview]);
  const statusChips = useMemo(() => {
    const chips = buildInsightStatusChips(overview);
    if (isMistral && !aiLoading) {
      return [{ label: "Mistral AI Assisted", className: "green" }, ...chips];
    }
    return chips;
  }, [overview, isMistral, aiLoading]);
  const kpis = useMemo(() => buildInsightKpis(overview), [overview]);
  const localSignals = useMemo(() => buildPrioritySignals(overview), [overview]);
  const signals = useMemo(
    () => pickMapped(localSignals, "insights", bulletsToSignalCards),
    [pickMapped, localSignals]
  );
  const localRootCauses = useMemo(() => buildRootCauseSignals(overview), [overview]);
  const rootCauses = useMemo(
    () => pickMapped(localRootCauses, "root", bulletsToRootCauseCards),
    [pickMapped, localRootCauses]
  );
  const localNarrative = useMemo(() => buildAiNarrativeSummary(overview), [overview]);
  const narrative = useMemo(
    () => pickMapped(localNarrative, "insights", bulletsToNarrativeItems),
    [pickMapped, localNarrative]
  );
  const localRecommendations = useMemo(() => buildRecommendations(overview), [overview]);
  const recommendations = useMemo(() => {
    const mapped = pickMapped(localRecommendations, "recs", bulletsToEnrolmentRecommendations);
    return mapped.map((item, index) => ({
      step: index + 1,
      title: item.title,
      body: item.body,
      timeline: localRecommendations[index]?.timeline || "30 days",
    }));
  }, [pickMapped, localRecommendations]);
  const localActionQueue = useMemo(() => buildActionQueue(overview), [overview]);

  const defaultRoot = useMemo(() => cardsToInsightLines(localRootCauses), [localRootCauses]);
  const defaultRecs = useMemo(() => cardsToInsightLines(localRecommendations), [localRecommendations]);
  const defaultActions = useMemo(
    () => registerToInsightLines(localActionQueue),
    [localActionQueue]
  );
  const actionQueue = useMemo(() => {
    const mapped = pickMapped(localActionQueue, "actions", bulletsToActionRegister);
    return mapped.map((row, index) => ({
      ...localActionQueue[index],
      ...row,
      due: localActionQueue[index]?.due || "30 days",
    }));
  }, [pickMapped, localActionQueue]);
  const impactMatrix = useMemo(() => buildImpactMatrix(overview), [overview]);

  return (
    <div className="tins-insights-tab" data-testid="teacher-insights">
      <section className="tins-hero-card" id="tins-hero">
        <div className="tins-insight-hero-layout">
          <div className="tins-hero-copy">
            <div className="tins-hero-kicker">
              <span className="tins-ai-badge">
                <Star size={16} />
                {aiLoading ? "Generating…" : isMistral ? "Mistral AI" : "Rule-based"}
              </span>
              <HeroStatusChip chipClass="tins-status-chip" variant="green" showChevron={false}>
                Updated Today
              </HeroStatusChip>
            </div>
            <h2>Teacher Intelligence Briefing</h2>
            <p>
              Converts the current teacher dashboard signals into a clear executive narrative: what
              is healthy, what is risky, why it is happening, and which actions should be taken
              first.
            </p>
            <div className="tins-status-row">
              {statusChips.map((chip) => {
                const actionable = /red|orange|amber/i.test(chip.className ?? "");
                return (
                  <HeroStatusChip
                    key={chip.label}
                    chipClass="tins-status-chip"
                    variant={chip.className || undefined}
                    onClick={actionable ? () => scrollToId("tins-actions") : undefined}
                    ariaLabel={actionable ? `${chip.label} — view teacher actions` : undefined}
                  >
                    {chip.label}
                  </HeroStatusChip>
                );
              })}
            </div>
          </div>

          <div className="tins-readiness-card" aria-label="Teacher readiness score">
            <div className="tins-gauge" style={gaugeStyle}>
              <div>
                <strong>{readiness}%</strong>
                <span>Readiness</span>
              </div>
            </div>
            <div className="tins-readiness-lines">
              {readinessLines.map((line) => (
                <div key={line.label} className="tins-readiness-line">
                  <div className="tins-meta">
                    <span>{line.label}</span>
                    <b>{line.value}</b>
                  </div>
                  <div className="tins-track">
                    <i className={line.barClass} style={{ width: `${line.width}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="tins-metric-grid" aria-label="Teacher insights KPIs">
        {kpis.map((card) => {
          const Icon = KPI_ICONS[card.iconClass] ?? Users;
          return (
            <article key={card.label} className="tins-metric-card">
              <div className="tins-metric-head">
                <small>{card.label}</small>
                <span className={`tins-metric-icon ${card.iconClass}`.trim()}>
                  <Icon size={20} />
                </span>
              </div>
              <strong>{card.value}</strong>
              <span>{card.sub}</span>
            </article>
          );
        })}
      </section>

      <div className="tins-section-title">
        <div>
          <h2>Priority Insight Signals</h2>
          <p>
            Instead of a long text accordion, the insights are grouped into action-oriented cards
            that leaders can scan in seconds.
          </p>
        </div>
        <button type="button" className="tins-section-action" onClick={() => scrollToId("tins-actions")}>
          View actions →
        </button>
      </div>

      <section className="tins-insight-grid">
        {signals.map((card) => {
          const Icon = SIGNAL_ICONS[card.iconClass] ?? Award;
          return (
            <article key={card.id} className="tins-signal-card">
              <div className="tins-signal-top">
                <span className={`tins-metric-icon ${card.iconClass}`.trim()}>
                  <Icon size={20} />
                </span>
                <span className={`tins-priority-badge ${card.badgeClass}`.trim()}>{card.badge}</span>
              </div>
              <h3>{toTitleCase(card.title)}</h3>
              <p>{card.body}</p>
              <div className="tins-signal-stats">
                {card.stats.map((stat) => (
                  <div key={stat.label} className="tins-mini-stat">
                    <b>{stat.value}</b>
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </section>

      <div className="tins-section-title">
        <div>
          <h2>Root-Cause Diagnostics</h2>
          <p>
            Grouped signals show why the teacher dashboard is underperforming and where the district
            can unlock the fastest improvement.
          </p>
        </div>
        <button
          type="button"
          className="tins-section-action"
          onClick={() => scrollToId("tins-recommendations")}
        >
          See recommendations →
        </button>
      </div>

      <section className="tins-two-column">
        <article className="tins-panel">
          <div className="tins-panel-head">
            <div>
              <h3>Root Cause Signals</h3>
              <p>Ranked by estimated impact on TQI and TRI.</p>
            </div>
            <span className="tins-pill">Diagnosis</span>
          </div>
          <div className="tins-cause-list">
            {rootCauses.map((item) => {
              const Icon = CAUSE_ICONS[item.iconClass] ?? Map;
              return (
                <div key={item.title} className="tins-cause-item">
                  <span className={`tins-metric-icon ${item.iconClass}`.trim()}>
                    <Icon size={20} />
                  </span>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </div>
                  <div className="tins-impact-score">
                    <b>{item.impact}</b>
                    <span>Impact</span>
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="tins-panel tins-brief-card">
          <div className="tins-panel-head">
            <div>
              <h3>AI Narrative Summary</h3>
              <p>Human-readable summary for district review meetings.</p>
            </div>
            <span className="tins-pill">Brief</span>
          </div>
          <div className="tins-brief-list">
            {narrative.map((item) => (
              <div key={item.title} className="tins-brief-item">
                <span className="tins-brief-dot" />
                <div>
                  <b>{item.title}</b>
                  <span>{item.body}</span>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="tins-action-plan">
        <InterventionActionPlan
          aiContent={aiMarkdown}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="tins-section-title" id="tins-recommendations">
        <div>
          <h2>Recommendations and Interventions</h2>
          <p>
            Recommended actions are converted into timed, accountable workstreams that can be
            assigned to education officers.
          </p>
        </div>
        <button type="button" className="tins-section-action" onClick={() => scrollToId("tins-queue")}>
          Open queue →
        </button>
      </div>

      <section className="tins-recommendation-grid">
        {recommendations.map((item) => (
          <article key={item.step} className="tins-recommend-card">
            <div className="tins-step">{item.step}</div>
            <h3>{toTitleCase(item.title)}</h3>
            <p>{item.body}</p>
            <span className="tins-time-chip">{item.timeline}</span>
          </article>
        ))}
      </section>

      <section className="tins-two-column" id="tins-actions">
        <article className="tins-panel tins-table-card" id="tins-queue">
          <div className="tins-table-head">
            <div>
              <h3>Insight Action Queue</h3>
              <p>Prioritized actions for district and block review.</p>
            </div>
            <span className="tins-pill">Next steps</span>
          </div>
          <table className="tins-action-table" aria-label="Teacher insight action queue">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Theme</th>
                <th>Signal</th>
                <th>Owner</th>
                <th>Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {actionQueue.map((row) => (
                <tr key={row.priority}>
                  <td>{row.priority}</td>
                  <td>{row.theme}</td>
                  <td>{row.signal}</td>
                  <td>{row.owner}</td>
                  <td>{row.due}</td>
                  <td>
                    <span className={`tins-tag ${row.statusClass}`.trim()}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>

        <article className="tins-panel">
          <div className="tins-panel-head">
            <div>
              <h3>Improvement Impact Matrix</h3>
              <p>Estimated intervention potential by teacher improvement theme.</p>
            </div>
            <span className="tins-pill">Impact</span>
          </div>
          <div className="tins-matrix">
            {impactMatrix.map((row) => (
              <div key={row.label} className="tins-matrix-row">
                <div className="tins-matrix-label">{row.label}</div>
                <div className="tins-matrix-track">
                  <div
                    className={`tins-matrix-bar ${row.barClass}`.trim()}
                    style={{ width: `${row.value}%` }}
                  />
                </div>
                <div className="tins-matrix-value">{row.value}</div>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default TeacherInsightsTab;
